<script setup lang="ts">
import EventLogApi from '@zyro-inc/site-modules/api/EventLogApi';
import ProductReview from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/ProductReview.vue';
import Carousel from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/Carousel.vue';
import OptionSelect from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/OptionSelect.vue';
import ReviewStars from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/ReviewStars.vue';
import GridButton from '@zyro-inc/site-modules/components/elements/button/GridButton.vue';
import ProductReviewAnalytics from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/ProductReviewAnalytics.vue';
import BlockEcommerceProductList from '@zyro-inc/site-modules/components/blocks/ecommerce/BlockEcommerceProductList.vue';
import { formatPrice } from '@zyro-inc/site-modules/utils/ecommerce/priceFormatter';
import { objectToCssVariablesDeprecated } from '@zyro-inc/site-modules/utils/objectToCssVariablesDeprecated';
import {
	EcommerceProductListScrollBehaviour,
	ImageHoverEffect,
	ImageRatioOption,
} from '@hostinger/builder-schema-validator/schema/schemaTypes';
import {
	SITE_PRODUCT_SELECTION,
	SITE_PRODUCT_SELECTION_TYPE_LOWEST,
	MAX_PRODUCTS_IN_CART,
	ECOMERCE_PRODUCTS_FREE_TYPES,
	DEFAULT_EMPTY_APPOINTMENT_VALUE,
} from '@zyro-inc/site-modules/constants/ecommerce';
import {
	EcommerceProductType,
	EcommerceProduct,
	EcommerceProductVariantQuantity,
	EcommerceProductOptionValue,
	EcommerceProductVariant,
	EcommerceProductAdditionalInfo,
	EcommerceQuantifiedItem,
	EcommerceRegion,
	EcommerceUpdateCartDataProps,
	EcommerceCart,
	EcommerceProductReview,
} from '@zyro-inc/site-modules/types';

import {
	getFormattedBookingDuration,
	isProductPriceRangeShown,
	getVariantQuantity,
} from '@zyro-inc/site-modules/components/blocks/ecommerce/utils';
import ProductSkeletonLoader from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/ProductSkeletonLoader.vue';
import QuantityPicker from '@zyro-inc/site-modules/components/ecommerce/-partials/QuantityPicker.vue';
import AdditionalInfoSections from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/AdditionalInfoSections.vue';
import {
	computed,
	watch,
	ref,
	onMounted,
} from 'vue';
import {
	DATA_ATTRIBUTE_ANIMATION_ROLE,
	DATA_ATTRIBUTE_ANIMATION_ROLE_BLOCK_ELEMENT,
} from '@zyro-inc/site-modules/constants/siteModulesConstants';
import CustomField from '@zyro-inc/site-modules/components/blocks/ecommerce/-partials/CustomField.vue';
import { isProductPage } from '@zyro-inc/site-modules/utils/ecommerce/common';
import { useProductReviewsList } from '@zyro-inc/site-modules/use/useProductReviewsList';
import { useEcommerceModal } from '@zyro-inc/site-modules/components/ecommerce/modals/useEcommerceModal';

const MAX_STOCK_AMOUNT_TO_SHOW = 5;
const DEFAULT_PICKER_VALUE = 1;
const CUSTOM_ATTRIBUTES = {
	[DATA_ATTRIBUTE_ANIMATION_ROLE]: DATA_ATTRIBUTE_ANIMATION_ROLE_BLOCK_ELEMENT,
};

const PRODUCT_TYPES_WITHOUT_QUANTITY_PICKER = [
	EcommerceProductType.BOOKING,
	EcommerceProductType.DIGITAL,
];

interface Props {
	blockId: string;
	productData: EcommerceProduct;
	relatedProducts?: EcommerceProduct[];
	relatedProductsTitle?: string;
	blockStyle?: Record<string, string>,
	textColorVars?: Record<string, string>,
	blockButtonText?: string,
	blockButtonStyle?: Record<string, string>,
	blockButtonType?: string,
	blockButtonBorderWidth?: number,
	navigationArrowsColor?: string,
	navigationThumbnailArrowsColor?: string,
	galleryPlacement?: string,
	imageRatio?: ImageRatioOption,
	imageBorderRadius?: string,
	isLoading?: boolean,
	isCheckoutLoading?: boolean,
	canAddToCart?: (product: EcommerceProduct, productVariantId: string) => boolean,
	isEager?: boolean,
	translations: Record<string, string>,
	quantifiedCartItemsList?: EcommerceQuantifiedItem[],
	isQuantityPickerEnabled: boolean,
	legacyProductPages?: Record<string, {[key: string]: any}> | {},
	isQuickPreview?: boolean,
	isCartVisible: boolean,
	siteId: string,
	shoppingCartItems?: Array<EcommerceProduct> | [],
	variantsQuantity: Array<EcommerceProductVariantQuantity>,
	currentPageType?: string,
	isMobileView?: boolean,
	successUrl?: string,
	updateCartData?:(payload: EcommerceUpdateCartDataProps) => Promise<EcommerceCart | undefined>,
	stripePublicKey?: string,
	storeId: string,
	isInEditor?: boolean,
	regions?: EcommerceRegion[],
	relatedProductsPageCount?: number,
	relatedProductsCurrentPage: number,
	isRelatedProductsLoading: boolean,
	isRelatedProductLinkDisabled?: boolean,
	isProductReviewEnabled?: boolean,
	isReviewsLoading?: boolean,
	productReviewsList?: EcommerceProductReview[],
	googleAnalytics?: string,
}

const props = withDefaults(defineProps<Props>(), {
	imageRatio: ImageRatioOption.COVER,
	imageBorderRadius: '0%',
	currentPageType: 'default',
	blockStyle: () => ({}),
	textColorVars: () => ({}),
	blockButtonStyle: () => ({}),
	blockButtonText: '',
	blockButtonType: 'primary',
	blockButtonBorderWidth: 0,
	navigationArrowsColor: '',
	navigationThumbnailArrowsColor: '',
	galleryPlacement: '',
	canAddToCart: () => true,
	shoppingCartItems: () => [],
	isLoading: false,
	isCheckoutLoading: false,
	isEager: false,
	quantifiedCartItemsList: () => [],
	legacyProductPages: () => ({}),
	isQuickPreview: false,
	isProductReviewEnabled: false,
	isReviewsLoading: false,
	googleAnalytics: '',
});

const emit = defineEmits<{
	'buy-button-click': [EcommerceProduct[]],
	'click-more': [],
	'image-click': [number],
	'open-cart': [],
	'get-regions': [],
	'related-product-page-changed': [number],
	'related-product-click': [EcommerceProduct],
	'add-related-product-to-bag': [EcommerceProduct],
	'load-more-reviews': [],
}>();

const { openEcommerceModal } = useEcommerceModal();

const {
	fetchProductReviewsChunk,
	resetProductReviewsList,
	productReviewsList: filteredProductReviewsList,
	count,
	rating,
} = useProductReviewsList();

const selectedQuantity = ref(1);
const isMounted = ref(false);
const variantImage = ref('');
const isLimitReached = ref(false);
const isButtonClicked = ref(false);
const selectedVariant = ref<EcommerceProduct>(props.productData || DEFAULT_EMPTY_APPOINTMENT_VALUE);
const isAddToBagEnabled = ref(true);
const blockProductRef = ref<HTMLElement | null>(null);

const displayedProductReviewsList = computed(() => {
	if (rating.value) {
		return filteredProductReviewsList.value;
	}

	return props.productReviewsList;
});
const isBlockInProductPage = computed(() => isProductPage(props.currentPageType));
const buttonBackgroundColor = computed(() => ({
	normal: props.blockButtonStyle[`grid-button-${props.blockButtonType}-background-color`],
	hover: props.blockButtonStyle[`grid-button-${props.blockButtonType}-background-color-hover`],
}));
const buttonBorderColor = computed(() => ({
	hover: props.blockButtonStyle[`grid-button-${props.blockButtonType}-border-color-hover`],
	normal: props.blockButtonStyle[`grid-button-${props.blockButtonType}-border-color`],
}));
const textAlign = computed(() => props.blockStyle?.textAlign);

const productData = computed(() => props.productData);
const selectedValueVariant = computed(() => selectedVariant.value?.variants[0]);
const priceData = computed(() => (selectedVariant.value.id
	? selectedValueVariant.value?.prices[0]
	: productData.value?.variants[0]?.prices?.[0]));

const isFreeTypeProduct = computed(() => {
	if (!productData.value) return false;

	return ECOMERCE_PRODUCTS_FREE_TYPES.includes(productData.value.type.value);
});
const isAddToCartDisabled = computed(
	() => {
		if (!props.productData) return true;

		return !props.canAddToCart(props.productData, selectedVariant.value?.variants[0].id);
	},
);
const isFreeTypeProductFree = computed(() => isFreeTypeProduct.value && !priceData.value?.amount);

const isViewMoreReviewsVisible = computed(() => {
	if (!productData.value) return false;

	if (!displayedProductReviewsList.value || !productData.value.reviewsAnalytics?.total_reviews) {
		return false;
	}

	if (rating.value) {
		return count.value > displayedProductReviewsList.value.length;
	}

	return productData.value.reviewsAnalytics.total_reviews > displayedProductReviewsList.value.length;
});

const mapOptionValue = (optionValue: EcommerceProductOptionValue) => optionValue;

const filterUnique = <T, >(keySelector: (item: T) => any) => (item: T, index: number, self: T[]) => self.findIndex(
	(other) => keySelector(other) === keySelector(item),
) === index;

const uniqueOptionSelections = computed<Array<{[key: number]: EcommerceProductOptionValue}>>(() => {
	if (!productData.value?.options.length) {
		return [];
	}

	return productData.value.options.map((option, index) => ({
		...option.values
			.map(mapOptionValue)
			.filter(filterUnique((v) => v.value))
			.filter((o) => {
				if (index === 0) {
					return true;
				}

				return productData.value.variants.some((variant) => productData.value.options
					.slice(0, index + 1)
					.every(({ id }, optionIndex) => {
						const selectedOptionValue = selectedValueVariant.value?.options
							.find((v) => v.option_id === id)?.value;

						const optionValueToCheck = optionIndex === index ? o.value : selectedOptionValue;

						return variant.options.some((v) => v.option_id === id && v.value === optionValueToCheck);
					}));
			}),
	}));
});

const isPurchasable = computed(() => props.productData?.purchasable === undefined || props.productData.purchasable);

const selectedOption = (index: number) => {
	const optionValue = selectedValueVariant.value?.options.find(
		(option) => option.option_id === uniqueOptionSelections.value[index]?.[0]?.option_id,
	);
	const mappedOptionValue = optionValue && mapOptionValue(optionValue);

	return Object.values(uniqueOptionSelections.value[index]).find((option) => option.value === mappedOptionValue?.value)?.id;
};

const quantityInCart = computed(() => props.quantifiedCartItemsList
	.find((item) => item.product?.variants?.[0]?.id === selectedValueVariant.value?.id)?.quantity || 0);

const productPageUrl = computed(() => {
	if (!productData.value) return null;

	return `${window.location.origin}/${productData.value.seo_settings.slug}`;
});

const totalQuantitySelected = computed(() => {
	if (!props.isCartVisible) {
		return selectedQuantity.value;
	}

	return selectedQuantity.value + quantityInCart.value;
});
const isStockAvailable = computed(() => {
	if (selectedValueVariant.value?.manage_inventory) {
		return totalQuantitySelected.value < getVariantQuantity({
			variantsQuantity: props.variantsQuantity,
			variantId: selectedValueVariant.value?.id,
		});
	}

	return true;
});
const computedStyles = computed(() => ({
	...objectToCssVariablesDeprecated({
		...props.textColorVars,
		...props.blockButtonStyle,
	}),
}));
const isPriceShown = computed(() => !productData.value?.options.length || (productData.value?.options.length && selectedVariant.value));

const buttonText = computed(() => {
	if (props.blockButtonText) {
		return props.blockButtonText;
	}

	return props.translations?.bookNow || 'Book now';
});

const formattedPrice = computed(() => (isFreeTypeProductFree.value
	? props.translations?.free || 'Free'
	: formatPrice({
		amount: priceData.value?.amount,
		currency: priceData.value?.currency,
	})));

const location = computed(() => productData.value?.variants?.[0]?.booking_event?.location);

const formattedBookingDuration = computed(() => {
	if (!productData.value) return '';

	return getFormattedBookingDuration(productData.value, props.translations);
});
const isOutOfStock = computed(() => selectedValueVariant.value?.manage_inventory && getVariantQuantity({
	variantsQuantity: props.variantsQuantity,
	variantId: selectedValueVariant.value?.id,
}) === 0);
const isStockInfoShown = computed(() => !!selectedValueVariant.value?.manage_inventory);
const stockInfoText = computed(() => {
	const quantity = getVariantQuantity({
		variantsQuantity: props.variantsQuantity,
		variantId: selectedValueVariant.value?.id,
	});

	if (isOutOfStock.value || quantityInCart.value === quantity) {
		return props.translations.outOfStock;
	}

	const prefix = quantity <= MAX_STOCK_AMOUNT_TO_SHOW ? quantity : `${MAX_STOCK_AMOUNT_TO_SHOW}+`;

	return `${prefix} ${props.translations.inStock} `;
});
const productAdditionalInfo = computed<EcommerceProductAdditionalInfo[]>(
	() => [...(productData.value?.additional_info || [])].sort((a, b) => a.order - b.order),
);
const isDescriptionVisible = computed(() => productData.value?.description && !props.isQuickPreview);
const isAdditionalInfoVisible = computed(() => productAdditionalInfo.value && !props.isQuickPreview);
const isDescriptionShownOnMobileWithAdditionalSections = computed(() => productAdditionalInfo.value.length && props.isMobileView);
const productImages = computed(() => productData.value?.images || productData.value?.media || []);
const customFields = computed(() => productData.value?.custom_fields || []);
const isQuantityPickerShown = computed(() => props.isQuantityPickerEnabled && !isFreeTypeProductFree.value);
const isQuantityPickerVisible = computed(() => !!productData.value
	&& !(PRODUCT_TYPES_WITHOUT_QUANTITY_PICKER.includes(productData.value.type.value))
	&& isQuantityPickerShown.value
	&& isPurchasable.value);

const getProductWithSelectedVariant = (variant: EcommerceProductVariant) => ({
	...(productData.value ?? {} as EcommerceProduct),
	variants: [variant],
});
const getInitiallySelectedVariant = (): EcommerceProduct => {
	if (!productData.value) {
		return DEFAULT_EMPTY_APPOINTMENT_VALUE as EcommerceProduct;
	}

	// Priority 1: Respect explicit lowest price configuration
	if (productData.value[SITE_PRODUCT_SELECTION] === SITE_PRODUCT_SELECTION_TYPE_LOWEST) {
		const lowestPriceVariant = productData.value.variants.reduce((acc, curr) => {
			const accPrice = acc.prices[0]?.sale_amount ?? acc.prices[0]?.amount ?? 0;
			const currPrice = curr.prices[0]?.sale_amount ?? curr.prices[0]?.amount ?? 0;

			return currPrice < accPrice ? curr : acc;
		});

		return getProductWithSelectedVariant(lowestPriceVariant);
	}

	// Priority 2: Match variant to displayed image
	const displayedImages = [
		productData.value.thumbnail,
		...(productData.value.images || []),
		...(productData.value.media || []),
	].filter(Boolean)
		.map((img) => (typeof img === 'string' ? img : img?.url))
		.filter(Boolean);

	if (displayedImages.length > 0) {
		const matchingVariant = displayedImages
			.map((imageUrl) => productData.value.variants.find(
				(variant) => variant.image_url === imageUrl,
			))
			.find((variant) => variant);

		if (matchingVariant) return getProductWithSelectedVariant(matchingVariant);
	}

	// Priority 3: Check for in-stock variant if no image match
	const inStockVariant = productData.value.variants.find(
		(variant) => !variant.manage_inventory || getVariantQuantity({
			variantsQuantity: props.variantsQuantity,
			variantId: variant.id,
		}) > 0,
	);

	if (inStockVariant) return getProductWithSelectedVariant(inStockVariant);

	// Priority 4: Fallback - preselect matching variant by default options
	const preselectedVariant = productData.value.variants
		.find((variant) => variant.options
			.every((option) => uniqueOptionSelections.value
				.some((selection) => selection[0]?.value === option?.value)));

	const productVariant = (preselectedVariant || productData.value.variants[0]) as EcommerceProductVariant;

	return getProductWithSelectedVariant(productVariant);
};

const handleButtonClick = () => {
	isButtonClicked.value = true;

	if (!isAddToBagEnabled.value) {
		return;
	}

	const productForShoppingCart = new Array(selectedQuantity.value).fill(selectedVariant.value);

	EventLogApi.logEvent({
		eventName: 'website_builder.user_site.add_to_cart_enter',
		eventProperties: {
			product_type: productForShoppingCart[0].type.value,
		},
	});

	const isSelectedProductTypeDigital = productForShoppingCart[0].type.value === EcommerceProductType.DIGITAL;

	if (isSelectedProductTypeDigital) {
		const isProductAlreadyInCart = props.shoppingCartItems.some((item) => item.id === productForShoppingCart[0].id);

		if (isProductAlreadyInCart) {
			emit('open-cart');

			return;
		}
	}

	selectedQuantity.value = DEFAULT_PICKER_VALUE;

	emit('buy-button-click', productForShoppingCart);
};

const handleVariantOptionChange = (id: string) => {
	selectedQuantity.value = DEFAULT_PICKER_VALUE;

	const optionSelection: EcommerceProductOptionValue = productData.value?.options.map(
		(option) => option.values.find((optionValue) => optionValue.id === id),
	).find((item) => item) || {} as EcommerceProductOptionValue;

	const remainingVariantOptions = selectedValueVariant.value?.options
		.filter((option) => option.option_id !== optionSelection.option_id);

	const allVariantOptions = [
		...remainingVariantOptions,
		optionSelection,
	];

	// find correct variant with the same option values
	const matchedVariant = productData.value?.variants.find((variant) => variant.options
		.every((option) => allVariantOptions
			.some((value) => value.value === option.value && value.option_id === option.option_id)));

	const defaultVariant = productData.value?.variants.find((variant) => variant.options
		.some((option) => option.option_id === optionSelection.option_id && option.value === optionSelection.value));

	const variantToSelect = matchedVariant ?? defaultVariant;

	if (variantToSelect) {
		selectedVariant.value = getProductWithSelectedVariant(variantToSelect);
	}
};

const handleQuantityChange = (value: number) => {
	const isQuantityTracked = selectedValueVariant.value?.manage_inventory;

	isLimitReached.value = (props.shoppingCartItems.length + value) >= MAX_PRODUCTS_IN_CART;

	if (isQuantityTracked) {
		const maxQuantity = getVariantQuantity({
			variantsQuantity: props.variantsQuantity,
			variantId: selectedValueVariant.value?.id,
		});

		if ((quantityInCart.value + value) > maxQuantity || isLimitReached.value) {
			// limit is the smaller value - either available stock amount or available cart amount
			const limit = Math.min(maxQuantity - quantityInCart.value, MAX_PRODUCTS_IN_CART - props.shoppingCartItems.length);

			selectedQuantity.value = limit;

			return;
		}
	} else if (isLimitReached.value) {
		selectedQuantity.value = MAX_PRODUCTS_IN_CART - props.shoppingCartItems.length;

		return;
	}

	if (value <= 0) {
		selectedQuantity.value = DEFAULT_PICKER_VALUE;

		return;
	}

	selectedQuantity.value = value;
};

const handleCustomFieldUpdate = (value: {message: string, errors: number}, editedFieldIndex: number) => {
	isAddToBagEnabled.value = value.errors === 0;

	if (!isAddToBagEnabled.value) {
		return;
	}

	selectedVariant.value = {
		...selectedVariant.value,
		custom_fields: customFields.value.map((field, index) => {
			if (index !== editedFieldIndex) {
				return field;
			}

			return {
				...field,
				value: value.message,
			};
		}),
	};
};

const handleLeaveReview = () => {
	openEcommerceModal('EcommerceSubmitReviewModal');
};

const setInitialProductData = () => {
	selectedVariant.value = getInitiallySelectedVariant();
	isAddToBagEnabled.value = !props.productData?.custom_fields?.some((field) => field.is_required && !field.value);
};

const handleRatingClick = (selectedRating: number | null) => {
	if (!props.productData?.id) return;

	if (selectedRating === rating.value || !selectedRating) {
		resetProductReviewsList();

		return;
	}

	resetProductReviewsList();

	rating.value = selectedRating;

	fetchProductReviewsChunk({
		storeId: props.storeId,
		productId: props.productData.id as string,
	});
};

onMounted(() => {
	isMounted.value = true;

	if (productData.value) {
		setInitialProductData();
	}
});

watch(productData, (newValue, oldValue) => {
	if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
		setInitialProductData();

		// component is not re-created when navigating from product page to product page, so we need to reset the product reviews list
		resetProductReviewsList();
	}
});
watch(selectedVariant, (newValue, oldValue) => {
	if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
		if (newValue?.variants[0]?.image_url) {
			variantImage.value = newValue.variants[0].image_url;
		}
	}
});

const handleProductClick = (product: EcommerceProduct) => {
	blockProductRef.value?.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
	});
	emit('related-product-click', product);
};

const handleLoadMoreReviews = () => {
	if (!props.productData?.id) return;

	if (!rating.value) {
		emit('load-more-reviews');

		return;
	}

	fetchProductReviewsChunk({
		storeId: props.storeId,
		productId: props.productData.id as string,
	});
};

const handlePreviewImageChanged = (index: number) => {
	const variantsWithSelectedImage = props.productData?.variants?.filter(
		(variant) => variant?.image_url === productImages.value[index]?.url,
	);

	if (variantsWithSelectedImage.length) {
		const countMatchingOptions = (variant: EcommerceProductVariant) => variant.options.filter(
			(option) => selectedValueVariant.value?.options.some(
				(o) => o.option_id === option.option_id && o.value === option.value,
			),
		).length;

		const variantToSelect = variantsWithSelectedImage.reduce((bestMatch: EcommerceProductVariant | null, currentVariant) => {
			const currentMatchCount = countMatchingOptions(currentVariant);
			const bestMatchCount = bestMatch ? countMatchingOptions(bestMatch) : 0;

			return currentMatchCount > bestMatchCount ? currentVariant : bestMatch;
		}, null);

		selectedVariant.value = getProductWithSelectedVariant(variantToSelect ?? variantsWithSelectedImage[0]);
	}
};

watch(() => props.shoppingCartItems, (newValue) => {
	const itemCount = newValue?.length || 0;

	// Since there is no 'this' in Vue 3 setup, we access the ref properties directly.
	selectedQuantity.value = DEFAULT_PICKER_VALUE;
	isLimitReached.value = (itemCount + selectedQuantity.value) >= MAX_PRODUCTS_IN_CART;
}, {
	immediate: true,
});
</script>

<template>
	<div
		:id="blockId"
		v-bind="CUSTOM_ATTRIBUTES"
		ref="blockProductRef"
		class="block-appointment-wrapper"
	>
		<ProductSkeletonLoader
			v-if="isLoading"
			:image-border-radius="imageBorderRadius"
			:text-align="textAlign"
		/>
		<div
			v-else
			class="block-appointment"
			:class="{
				'block-appointment--centered': textAlign === 'center',
				'block-appointment--in-preview': isQuickPreview
			}"
			:style="computedStyles"
		>
			<div class="block-appointment__main-info">
				<div class="block-appointment__image-content-wrapper">
					<Carousel
						:media-items="productImages"
						:product-title="productData?.title || ''"
						:arrows-color="navigationArrowsColor"
						:navigation-thumbnail-arrows-color="navigationThumbnailArrowsColor"
						:gallery-placement="galleryPlacement"
						:image-ratio="imageRatio"
						:image-border-radius="imageBorderRadius"
						:is-eager="isEager"
						:site-id="siteId"
						:variant-image="variantImage"
						:is-quick-preview="isQuickPreview"
						:is-variant-image-preselected="isProductPriceRangeShown(productData)"
						@image-click="emit('image-click', $event)"
						@preview-image-changed="handlePreviewImageChanged"
					/>
					<p
						v-if="productData?.description && isDescriptionVisible && productAdditionalInfo.length && !isMobileView"
						:key="productData.description + isMounted"
						class="block-appointment__description block-appointment__description--under-image"
						v-html="productData.description"
					/>
				</div>
				<div class="block-appointment__content-wrapper">
					<div class="block-appointment__price-data-wrapper">
						<component
							:is="isBlockInProductPage ? 'h1' : 'h2'"
							v-qa="'builder-product-section-title'"
							class="block-appointment__title"
						>
							{{ productData?.title }}
						</component>
						<div
							v-if="isProductReviewEnabled && productData?.reviewsAnalytics"
							class="block-appointment__rating-below-title"
						>
							<ReviewStars
								class="block-appointment__rating"
								:rating="productData.reviewsAnalytics.average_rating"
								is-half-allowed
							/>
							<p class="block-appointment__rating-count">
								({{ productData.reviewsAnalytics?.total_reviews }})
							</p>
						</div>
						<component
							:is="isBlockInProductPage ? 'h2' : 'h3'"
							v-if="!isQuickPreview && productData?.subtitle"
							class="block-appointment__subtitle"
						>
							{{ productData.subtitle }}
						</component>
						<p
							v-if="Object.keys(priceData || {})?.length"
							class="block-appointment__price-wrapper block-appointment__price-wrapper--with-duration"
						>
							<span
								v-if="isPriceShown"
								class="block-appointment__price body-large"
								:class="{ 'block-appointment__price--sale': priceData?.sale_amount }"
							>
								{{ formattedPrice }}
							</span>
							<span
								v-if="priceData?.sale_amount"
								class="block-appointment__price body-large"
								:class="{ 'block-appointment__price--sale-seperator': priceData?.sale_amount }"
							>
								-
							</span>
							<span class="block-appointment__additional-info">
								<span
									v-if="priceData?.sale_amount && isPriceShown"
									class="block-appointment__price body-large"
								>
									{{ formatPrice({
										amount: priceData.sale_amount,
										currency: priceData.currency
									}) }}
								</span>
							</span>
						</p>

						<p class="block-appointment__duration">
							<span class="block-appointment__duration-label">{{ translations.duration || 'Duration' }} - </span>
							<span class="block-appointment__duration-value">{{ formattedBookingDuration }}</span>
						</p>

						<p class="block-appointment__location">
							<span class="block-appointment__location-label">{{ translations.location || 'Location' }} - </span>
							<span class="block-appointment__location-value">{{ location }}</span>
						</p>
						<div
							v-if="productData?.options.length"
							class="block-appointment__input-field-wrapper"
						>
							<OptionSelect
								v-for="(option, index) in productData.options"
								:key="`option-${index}`"
								:value="selectedOption(index)"
								:options="uniqueOptionSelections[index]"
								:title="option.title"
								class="block-appointment__input-field"
								:class="{ 'block-appointment__input-field--centered' : textAlign === 'center' }"
								label-key="value"
								@set-value="handleVariantOptionChange($event)"
							/>
						</div>
						<div
							v-if="customFields.length"
							class="block-appointment__input-field-wrapper"
						>
							<CustomField
								v-for="(field, index) in customFields"
								:key="`custom-field-${index}`"
								class="block-appointment__input-field"
								:class="{ 'block-appointment__input-field--centered' : textAlign === 'center' }"
								:custom-field="field"
								:translations="translations"
								:is-button-clicked="isButtonClicked"
								@update:model-value="handleCustomFieldUpdate($event, index)"
							/>
						</div>
						<div
							v-if="isQuantityPickerVisible"
							class="block-appointment__quantity-wrapper"
							:class="{ 'block-appointment__quantity-wrapper--disabled' : isOutOfStock || isAddToCartDisabled }"
						>
							<QuantityPicker
								qa-selector="productpage"
								:quantity="selectedQuantity"
								:is-limit-reached="isLimitReached"
								:is-stock-available="isStockAvailable"
								@quantity-change="handleQuantityChange"
							/>
							<p
								v-if="isStockInfoShown"
								class="block-appointment__stock-text"
							>
								{{ stockInfoText }}
							</p>
						</div>
					</div>
					<div
						v-if="isPurchasable"
						class="block-appointment__button-wrapper"
					>
						<GridButton
							v-qa="`appointmentsection-btn-addtobag`"
							:type="blockButtonType"
							:content="buttonText"
							class="block-appointment__button"
							:class="[`block-appointment__button--${blockButtonType}`,]"
							:is-loading="isCheckoutLoading"
							tag-name="button"
							:disabled="isAddToCartDisabled"
							:border-width="blockButtonBorderWidth"
							:border-color="buttonBorderColor.normal"
							:border-color-hover="buttonBorderColor.hover"
							:background-color="buttonBackgroundColor.normal"
							:background-color-hover="buttonBackgroundColor.hover"
							@click="handleButtonClick"
						/>
					</div>
					<p
						v-if="productData?.description
							&& isDescriptionVisible
							&& (isDescriptionShownOnMobileWithAdditionalSections || !productAdditionalInfo.length)
						"
						:key="productData.description + isMounted"
						v-qa="'builder-product-section-description'"
						class="block-appointment__description"
						v-html="productData.description"
					/>
					<AdditionalInfoSections
						v-if="isAdditionalInfoVisible && productData"
						:data="productAdditionalInfo"
						:product-description="productData.description"
					/>
					<a
						v-else-if="isQuickPreview && productPageUrl"
						:href="productPageUrl"
						class="block-appointment__link body-small"
					>
						{{ translations.moreDetails }}
					</a>
				</div>
			</div>
			<div
				v-if="relatedProducts?.length"
				class="block-appointment__related-products"
			>
				<BlockEcommerceProductList
					:block-id="blockId"
					:block-style="{
						...blockStyle,
						imageBorderRadius
					}"
					:text-color-vars="textColorVars"
					:is-product-list-shown="true"
					:products-per-page="4"
					:column-count="isMobileView ? 1 : 4"
					:page-count="relatedProductsPageCount ?? 1"
					:total-product-count="relatedProducts?.length ?? 0"
					:current-page="relatedProductsCurrentPage"
					:is-button-enabled="true"
					:button-display="'always'"
					:button-text="buttonText"
					:button-style="blockButtonStyle"
					:button-type="blockButtonType"
					:button-border-width="blockButtonBorderWidth"
					:is-loading="isRelatedProductsLoading"
					:is-categories-loaded="true"
					:is-list-centered="false"
					:ribbon-style="{}"
					:products="relatedProducts ?? []"
					:translations="translations"
					:image-ratio="imageRatio"
					:image-hover-effect="ImageHoverEffect.NO_EFFECT"
					:is-eager="false"
					:site-id="siteId"
					:variants-quantity="variantsQuantity"
					:product-sorting="{ enabled: false }"
					:product-search="{
						isEnabled: false,
						placeholderText: '',
						noResultsText: ''
					}"
					:background-color="''"
					:is-category-list-enabled="false"
					:is-full-width="true"
					:is-total-product-count-shown="false"
					:column-gap="24"
					:row-gap="24"
					:is-button-full-width="true"
					:is-mobile-view="isMobileView"
					:scroll-behaviour="EcommerceProductListScrollBehaviour.PAGINATION"
					:is-cart-visible="false"
					:is-dynamic-product-page-enabled="true"
					:is-whats-app-enabled="false"
					:is-scroll-to-top-enabled="false"
					:skip-pagination-scroll-to-top="true"
					:category-title="relatedProductsTitle"
					:remove-paddings="true"
					:is-product-list-item-link-disabled="isRelatedProductLinkDisabled"
					:is-product-review-enabled="isProductReviewEnabled"
					@button-click="(product) => emit('add-related-product-to-bag', product)"
					@page-changed="(page) => emit('related-product-page-changed', page)"
					@product-click="handleProductClick"
				/>
			</div>
			<template v-if="isProductReviewEnabled && isBlockInProductPage">
				<template v-if="productReviewsList?.length">
					<h3 class="block-appointment__reviews-title">
						{{ translations.reviewsTitle }}
					</h3>
					<div class="block-appointment__reviews">
						<ProductReviewAnalytics
							v-if="productData?.reviewsAnalytics"
							:average-rating="productData.reviewsAnalytics.average_rating"
							:total-reviews="productData.reviewsAnalytics.total_reviews"
							:rating-counts="[
								productData.reviewsAnalytics.rating_1_count,
								productData.reviewsAnalytics.rating_2_count,
								productData.reviewsAnalytics.rating_3_count,
								productData.reviewsAnalytics.rating_4_count,
								productData.reviewsAnalytics.rating_5_count,
							]"
							:translations="translations"
							:block-button-type="blockButtonType"
							:block-button-border-width="blockButtonBorderWidth"
							:button-border-color="buttonBorderColor.normal"
							:button-border-color-hover="buttonBorderColor.hover"
							:button-background-color="buttonBackgroundColor.normal"
							:button-background-color-hover="buttonBackgroundColor.hover"
							:filtered-rating="rating"
							@rating-click="handleRatingClick"
							@leave-review-click="handleLeaveReview"
						/>
						<ProductReview
							v-for="review in displayedProductReviewsList"
							:key="review.id"
							:review="review"
							:translations="translations"
						/>
						<GridButton
							v-if="isViewMoreReviewsVisible"
							v-qa="`appointmentsection-btn-loadmorereviews`"
							:content="translations.viewMore || 'View more'"
							class="block-appointment__button block-appointment__load-more-button"
							:is-loading="isReviewsLoading"
							tag-name="button"
							:type="blockButtonType"
							:border-width="blockButtonBorderWidth"
							:border-color="buttonBorderColor.normal"
							:border-color-hover="buttonBorderColor.hover"
							:background-color="buttonBackgroundColor.normal"
							:background-color-hover="buttonBackgroundColor.hover"
							@click="handleLoadMoreReviews"
						/>
					</div>
				</template>
				<div
					v-else
					class="block-appointment__reviews-empty"
				>
					<h3 class="block-appointment__reviews-empty-title">
						{{ translations.reviewsEmptyTitle }}
					</h3>
					<GridButton
						v-qa="`appointmentsection-btn-leavefirstreview`"
						:content="translations.reviewButton || 'Leave review'"
						:type="blockButtonType"
						:border-width="blockButtonBorderWidth"
						:border-color="buttonBorderColor.normal"
						:border-color-hover="buttonBorderColor.hover"
						:background-color="buttonBackgroundColor.normal"
						:background-color-hover="buttonBackgroundColor.hover"
						tag-name="button"
						@click="handleLeaveReview"
					/>
				</div>
			</template>
		</div>
	</div>
</template>

<style lang="scss">
@import "@zyro-inc/site-modules/scss/mixins/site-engine-mobile";
@import "@zyro-inc/site-modules/scss/mixins/font-style";
@import "@zyro-inc/site-modules/components/blocks/ecommerce/-partials/shared";
@include font-style("h3", ".block-appointment__title", ".block-appointment");
@include font-style("h3", ".block-appointment__reviews-title", ".block-appointment");
@include font-style("h3", ".block-appointment__reviews-empty-title", ".block-appointment");
@include font-style("h5", ".block-appointment__subtitle", ".block-appointment");
@include font-style("body", "p,.block-appointment__input-field-wrapper", ".block-appointment");
@include font-style("body-large", ".body-large", ".block-appointment");
@include font-style("body-small", ".body-small", ".block-appointment");

.block-appointment-wrapper {
	z-index: $z-index-site-engine-block-grid;
	padding: var(--block-padding);
}

.block-appointment {
	$this: &;

	flex-direction: column;
	justify-content: center;
	width: 100%;
	max-width: var(--content-width);
	margin: 0 auto;
	text-align: left;

	&--centered {
		text-align: center;

		#{$this}__content-wrapper,
		#{$this}__price-data-wrapper,
		#{$this}__button-wrapper {
			align-items: center;
			text-align: center;
		}
	}

	&--in-preview {
		min-height: 500px;
		max-height: 700px;

		#{$this}__content-wrapper {
			justify-content: flex-start;
		}

		#{$this}__price-data-wrapper {
			justify-content: flex-start;
			overflow: auto;
		}
	}

	&__related-products {
		margin-top: 80px;
	}

	&__reviews-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin-top: 80px;
	}

	&__reviews-empty-title {
		margin-bottom: 32px;
	}

	&__reviews-title {
		margin-top: 80px;
		margin-bottom: 48px;
	}

	&__rating-below-title {
		display: flex;
		margin-bottom: 16px;
	}

	&__rating {
		margin-right: 8px;
	}

	&__reviews {
		margin-top: 40px;
	}

	&__load-more-button {
		margin: 0 auto;
	}

	&__stock-text {
		display: flex;
		align-items: center;
		margin-left: 24px;
	}

	&__image-content-wrapper {
		width: 100%;
	}

	&__main-info {
		display: flex;
		width: 100%;
	}

	&__content-wrapper,
	&__price-data-wrapper {
		display: flex;
		flex-direction: column;
		align-items: baseline;
		justify-content: flex-start;
		width: 100%;
	}

	&__content-wrapper {
		max-width: 624px;
		padding-left: 80px;
		word-break: break-word;
	}

	&__button-wrapper {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		width: 100%;
		margin-top: 32px;
	}

	&__button {
		position: relative;
		display: flex;
		align-items: center;
		height: var(--button-height);

		&--primary {
			margin: calc(-1 * var(--grid-button-primary-border-null, var(--grid-button-primary-border-width, 0)));

			&:focus,
			&:hover {
				margin: calc(-1 * var(--grid-button-primary-border-null-hover, var(--grid-button-primary-border-width-hover, 0)));
			}
		}

		&--secondary {
			margin: calc(-1 * var(--grid-button-secondary-border-null, var(--grid-button-secondary-border-width, 0)));

			&:focus,
			&:hover {
				margin:
					calc(
						-1 * var(--grid-button-secondary-border-null-hover, var(--grid-button-secondary-border-width-hover, 0))
					);
			}
		}
	}

	&__title {
		margin-bottom: 8px;
	}

	&__subtitle {
		margin-bottom: 16px;
	}

	&__price-wrapper {
		display: flex;
		flex-wrap: wrap;

		&--with-duration {
			margin-bottom: 8px;
		}
	}

	&__additional-info {
		display: flex;
	}

	&__price {
		margin-bottom: 0;

		&--sale-seperator, &--sale {
			margin-right: 8px;
			opacity: 0.4;
		}

		&--sale {
			&#{&}#{&} {
				text-decoration: line-through;
			}
		}
	}

	&__duration {
		margin-bottom: 8px;
	}

	&__location-label,
	&__duration-label,
	&__notice {
		opacity: 0.6;
	}

	&__location-value,
	&__duration-value {
		font-weight: 600;
	}

	&__location-value {
		text-decoration: underline;
	}

	&__input-field-wrapper {
		width: 100%;
		margin-top: 32px;
	}

	&__input-field {
		&:not(:last-child) {
			margin-bottom: 24px;
		}

		&--centered {
			margin-right: auto;
			margin-left: auto;
		}
	}

	&__quantity-wrapper {
		display: flex;
		margin-top: 32px;

		&--disabled {
			pointer-events: none;
			filter: opacity(0.4);
		}
	}

	&__notice {
		margin-top: 8px;
		margin-bottom: 32px;
		font-size: 14px;
	}

	&__link {
		margin-block: 32px;

		&#{&} {
			text-decoration: underline;
		}
	}

	&__description {
		width: 100%;
		margin-top: 56px;
		word-break: break-word;
		white-space: pre-line;

		@include description-style;

		&--under-image {
			max-width: var(--max-width-description, 600px); // defined in shared scss
		}
	}
}

@include site-engine-mobile {
	.block-appointment-wrapper {
		padding: var(--m-block-padding);
		width: 100%;
	}

	.block-appointment {
		flex-direction: column;
		width: 100%;

		&__main-info {
			flex-direction: column;
		}

		&--centered {
			align-items: center;
		}

		&__content-wrapper {
			max-width: unset;
			padding: 32px 0 0;
		}

		&__reviews-title {
			margin-top: 40px;
			margin-bottom: 24px;
		}

		&__reviews-empty {
			margin-top: 40px;
		}
	}
}

.v-enter-active,
.v-leave-active {
	transition: opacity 0.3s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
}
</style>
